import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { Toast } from 'js/src/components/common/Toast';
import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates/templates-selectors';
import type { AnimateSelectionPlan } from 'js/src/data/transcript/transcript-types';
import { AnimateSelectionKind } from 'js/src/data/transcript/transcript-types';
import { checkpointVideo } from 'js/src/data/video-data';
import { MAX_SCENE_GROUPS_IN_VIDEO, MEDIA_ZOOM } from 'js/src/libs/consts';
import { createContainCropState } from 'js/src/libs/crop-utils';
import { getLinkedMediaAssets } from 'js/src/libs/scene-utils';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    applyUserSceneDurationOperation,
    createOverlayOperation,
    deleteLuminaryOverlayOperation,
    duplicateSceneGroupOperation,
    setUpMediaOnlyOverlayOperation,
    translateOverlayEndOperation,
} from 'js/src/luminary/luminary-operations/core';
import {
    getBaseSceneDesignId,
    getPrePackagedMorphPosition,
    getSceneWithBaseMediaAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isVoiceDrivenSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import { getMediaTransform, isMediaTransformCustomized } from 'js/src/luminary/luminary-utils';
import { getSceneSequenceMap } from 'js/src/luminary/morph-utils';
import { saveRawLuminary, setMediaCrop } from 'js/src/luminary/utils';
import {
    canCarryMorph,
    enableMorphFrom,
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    runVoicedrivenTranscriptBatchOperation,
    sameGroupNextScene,
    sameGroupPrevScene,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

type AnimatedZoomTarget = {
    sceneId: string;
    sceneGroupId: string;
    assetId: string;
};

const linkScenesForAnimatedZoomOperation = createLuminaryOperation(
    ({ luminary }, sceneIds: string[], linksToFollowingScene = false) => {
        luminary.batchRun(() => {
            sceneIds.forEach((sceneId, index) => {
                const scene = luminary.getSceneById(sceneId);
                if (!scene) {
                    return;
                }
                const isLast = index === sceneIds.length - 1;
                const linksForward = !isLast || linksToFollowingScene;
                scene.morphEnabled = linksForward;
                scene.isPrePackagedMorphWithNextScene = linksToFollowingScene;
                if (linksForward) {
                    scene.getAssets({ deep: true }).forEach((asset) => {
                        asset.morphInterpolation = VideoData.EASE_IN_OUT_INTERPOLATION_PRESET;
                    });
                }
                if (index > 0) {
                    scene.customTransition.transitionUrl = null;
                }
            });
        });
    },
);

const armMorphEdgeOperation = createLuminaryOperation(({ luminary }, fromSceneId: string) => {
    const from = luminary.getSceneById(fromSceneId);
    const to = from ? sameGroupNextScene(from) : null;
    if (!from || !to || !canCarryMorph(from) || !canCarryMorph(to)) {
        return;
    }
    enableMorphFrom(from);
    to.customTransition.transitionUrl = null;
});

const armAnimatedZoomOperation = createLuminaryOperation(({ luminary, call }, sceneId: string) => {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return;
    }
    luminary.batchRun(() => {
        const prev = sameGroupPrevScene(scene);
        if (prev) {
            call(armMorphEdgeOperation, prev.id);
        }
        call(armMorphEdgeOperation, sceneId);
    });
});

const fitMediaAssetInSceneOperation = createLuminaryOperation(
    ({ luminary }, sceneId: string, assetId: string) => {
        const asset = luminary.getSceneById(sceneId)?.getAssetById(assetId);
        if (!asset || !VideoData.isMediaAsset(asset) || !asset.sourceData) {
            return;
        }

        const contain = createContainCropState({
            assetWidth: asset.sourceData.width,
            assetHeight: asset.sourceData.height,
            viewportWidth: asset.mediaTargetResolution.width,
            viewportHeight: asset.mediaTargetResolution.height,
        });

        setMediaCrop(
            luminary,
            sceneId,
            assetId,
            contain.xTranslatePercent,
            contain.yTranslatePercent,
            Math.max(contain.zoom, MEDIA_ZOOM.MIN_VALUE),
            asset.transformation.angle,
            asset.flipHorizontally,
            asset.flipVertically,
        );
    },
);

function findMorphCounterpartAssetId(
    luminary: Parameters<typeof getSceneSequenceMap>[0],
    sceneId: string,
    assetId: string,
    targetSceneId: string,
): string | null {
    const mapped = getSceneSequenceMap(luminary, sceneId, assetId).get(targetSceneId);
    if (mapped) {
        return mapped;
    }

    // `load()` preserves asset names even though it regenerates ids, so names are a safe fallback.
    const sourceAsset = luminary.getSceneById(sceneId)?.getAssetById(assetId);
    if (!sourceAsset) {
        return null;
    }
    const counterpart = luminary
        .getSceneById(targetSceneId)
        ?.getAssets({ deep: true })
        .find((asset) => asset.name === sourceAsset.name);
    return counterpart?.id ?? null;
}

export const createAnimatedZoomFromSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        {
            sceneId,
            sceneGroupId,
            assetId,
            hasCustomCrop,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneId: string;
            sceneGroupId: string;
            assetId: string;
            hasCustomCrop: boolean;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): AnimatedZoomTarget | null => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const scene = luminary.getSceneById(sceneId);
        if (!sceneGroup || !scene) {
            return null;
        }

        if (isVoiceDrivenSceneGroup(sceneGroup)) {
            throw new Error('animated zoom cannot duplicate a voicedriven scene group');
        }

        if (luminary.sceneGroups.length >= MAX_SCENE_GROUPS_IN_VIDEO) {
            Toast.error(
                `You can have a maximum of ${MAX_SCENE_GROUPS_IN_VIDEO} scenes in your video. To create another scene, you must delete an existing scene.`,
            );
            return null;
        }

        const originalIndex = sceneGroup.index;
        const originalDuration = scene.duration;
        const insertBefore = hasCustomCrop && getPrePackagedMorphPosition(scene) === null;
        // Captured before the duplicate: whichever scene ends up last in the pair has to carry
        // this, so splicing into an existing chain doesn't cut it off at the new scene.
        const wasLinkedToNextScene = scene.isPrePackagedMorphWithNextScene;

        const target = luminary.batchRun((): AnimatedZoomTarget | null => {
            const newSceneGroupId = call(duplicateSceneGroupOperation, {
                sceneGroupId,
                scrollToNewSceneGroup: false,
                systemTriggered,
                parentActionGroup,
            });
            if (!newSceneGroupId) {
                return null;
            }

            if (insertBefore) {
                luminary.moveSceneGroupToIndex(newSceneGroupId, originalIndex);
            }

            const newSceneGroup = luminary.getSceneGroupById(newSceneGroupId);
            const newScene = newSceneGroup?.scenes[0];
            if (!newSceneGroup || !newScene) {
                return null;
            }

            const firstSceneId = insertBefore ? newScene.id : sceneId;
            const secondSceneId = insertBefore ? sceneId : newScene.id;

            call(
                linkScenesForAnimatedZoomOperation,
                [firstSceneId, secondSceneId],
                wasLinkedToNextScene,
            );

            const newAssetId = findMorphCounterpartAssetId(luminary, sceneId, assetId, newScene.id);
            if (!newAssetId) {
                return null;
            }

            // Halve rather than double: adding the zoom shouldn't stretch the video's runtime.
            const firstDuration = Math.round(originalDuration / 2);
            call(
                applyUserSceneDurationOperation,
                firstSceneId,
                firstDuration,
                systemTriggered,
                parentActionGroup,
            );
            call(
                applyUserSceneDurationOperation,
                secondSceneId,
                originalDuration - firstDuration,
                systemTriggered,
                parentActionGroup,
            );

            if (insertBefore) {
                call(fitMediaAssetInSceneOperation, firstSceneId, newAssetId);
            }

            return insertBefore
                ? { sceneId, sceneGroupId, assetId }
                : { sceneId: newScene.id, sceneGroupId: newSceneGroupId, assetId: newAssetId };
        });

        if (!target) {
            return null;
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        return target;
    },
);

const splitOverlayAtTranscriptTimesOperation = createLuminaryOperation(
    (
        { luminary, getState, call },
        {
            sceneId,
            sceneGroupId,
            boundaries,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneId: string;
            sceneGroupId: string;
            boundaries: number[];
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): string[] => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const scene = luminary.getSceneById(sceneId);
        if (!sceneGroup || !scene) {
            throw new Error('splitOverlay missing scene or scene group');
        }
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            throw new Error('splitOverlay only works for voice-driven scene groups');
        }

        const baseSceneDesignId = getBaseSceneDesignId(getState(), sceneGroup);
        if (!baseSceneDesignId) {
            throw new Error('Base scene design id not found');
        }

        // Capture before any mutation — creating an overlay after the source can resize the source.
        const sceneDesignId = scene.sceneDesign;
        const savedData = scene.save();

        const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const overlayEnd = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(scene),
            playbackRegions,
            sourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );

        // The caller decides where a cut is worth making, by looking at the words either side of
        // the region; nothing here is in a position to second-guess that.
        const sortedBoundaries = [...boundaries].sort((a, b) => a - b);

        if (sortedBoundaries.length === 0) {
            return [sceneId];
        }

        const pieceIds = [sceneId];
        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            // Shrink the original down to the first piece. This leaves a base scene covering the
            // rest of the original span, which each new overlay is then carved back out of.
            call(
                translateOverlayEndOperation,
                sceneId,
                sceneGroupId,
                baseSceneDesignId,
                sortedBoundaries[0],
                systemTriggered,
                parentActionGroup,
            );

            const pieceEdges = [...sortedBoundaries, overlayEnd];
            for (let i = 0; i < pieceEdges.length - 1; i++) {
                const newSceneId = call(
                    createOverlayOperation,
                    sceneGroupId,
                    pieceEdges[i],
                    pieceEdges[i + 1],
                    sceneDesignId,
                    systemTriggered,
                    parentActionGroup,
                );
                const newScene = luminary.getSceneById(newSceneId);
                if (!newScene) {
                    throw new Error('splitOverlay could not find the new overlay scene');
                }
                // .load()'ing changes the duration of the scene, so restore the fitted duration.
                const newDuration = getVoicedrivenSceneDuration(newScene);
                newScene.load(savedData);
                newScene.generateNewAssetIds();
                setVoicedrivenSceneDuration(newScene, newDuration);
                pieceIds.push(newSceneId);
            }
        });

        return pieceIds;
    },
);

export const createAnimatedZoomFromOverlayOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        {
            sceneId,
            sceneGroupId,
            assetId,
            boundaries,
            activePieceIndex,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneId: string;
            sceneGroupId: string;
            assetId: string;
            boundaries: number[];
            activePieceIndex: number;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): AnimatedZoomTarget | null => {
        // Captured before the split: the pieces take over the original overlay's span, so the last
        // of them inherits whatever the original linked forward to.
        const wasLinkedToNextScene = Boolean(
            luminary.getSceneById(sceneId)?.isPrePackagedMorphWithNextScene,
        );
        const hasCustomCrop = isMediaTransformCustomized(
            getMediaTransform(luminary.getSceneById(sceneId)?.getAssetById(assetId)),
        );

        const pieceIds = call(splitOverlayAtTranscriptTimesOperation, {
            sceneId,
            sceneGroupId,
            boundaries,
            systemTriggered,
            parentActionGroup,
        });

        if (pieceIds.length < 2) {
            call(armAnimatedZoomOperation, sceneId);
            dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(luminary),
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            return { sceneId, sceneGroupId, assetId };
        }

        call(linkScenesForAnimatedZoomOperation, pieceIds, wasLinkedToNextScene);

        const activeSceneId = pieceIds[Math.min(activePieceIndex, pieceIds.length - 1)];
        call(armAnimatedZoomOperation, activeSceneId);
        const assetIdByScene = new Map<string, string>([[sceneId, assetId]]);
        pieceIds.forEach((pieceId) => {
            if (pieceId === sceneId) {
                return;
            }
            const counterpart = findMorphCounterpartAssetId(luminary, sceneId, assetId, pieceId);
            if (counterpart) {
                assetIdByScene.set(pieceId, counterpart);
            }
        });

        if (!hasCustomCrop) {
            luminary.batchRun(() => {
                pieceIds.forEach((pieceId) => {
                    const pieceAssetId = assetIdByScene.get(pieceId);
                    if (!pieceAssetId || pieceId === activeSceneId) {
                        return;
                    }
                    call(fitMediaAssetInSceneOperation, pieceId, pieceAssetId);
                });
            });
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        const activeAssetId = assetIdByScene.get(activeSceneId);
        if (!activeAssetId) {
            return null;
        }
        return { sceneId: activeSceneId, sceneGroupId, assetId: activeAssetId };
    },
);

const linkedMediaAssetId = (scene: Scene | undefined): string | null =>
    scene ? (getLinkedMediaAssets(scene)[0]?.id ?? null) : null;

export const createAnimatedZoomFromSelectionOperation = createLuminaryOperation(
    (
        { luminary, getState, dispatch, call },
        {
            sceneGroupId,
            plan,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            plan: AnimateSelectionPlan;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
    ): AnimatedZoomTarget | null => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
            return null;
        }

        if (plan.kind === AnimateSelectionKind.SplitOverlay) {
            const assetId = linkedMediaAssetId(luminary.getSceneById(plan.sceneId));
            if (!assetId) {
                return null;
            }
            return call(createAnimatedZoomFromOverlayOperation, {
                sceneId: plan.sceneId,
                sceneGroupId,
                assetId,
                boundaries: plan.boundaries,
                activePieceIndex: plan.activePieceIndex,
                systemTriggered,
                parentActionGroup,
            });
        }

        const cutawaySceneDesignId = getTemplateDefaultCutawaySceneDesign(
            getState(),
            luminary.template.id,
        );
        if (!cutawaySceneDesignId) {
            return null;
        }

        const sceneWithBaseMediaAsset = getSceneWithBaseMediaAsset(sceneGroup);

        if (plan.replacesSceneId) {
            call(
                deleteLuminaryOverlayOperation,
                plan.replacesSceneId,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
        }

        let zoomSceneId = '';
        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            zoomSceneId = call(
                createOverlayOperation,
                sceneGroupId,
                plan.startTime,
                plan.endTime,
                cutawaySceneDesignId,
                systemTriggered,
                parentActionGroup,
            );
            call(setUpMediaOnlyOverlayOperation, zoomSceneId, sceneWithBaseMediaAsset);
        });

        call(armAnimatedZoomOperation, zoomSceneId);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        const assetId = linkedMediaAssetId(luminary.getSceneById(zoomSceneId));
        return assetId ? { sceneId: zoomSceneId, sceneGroupId, assetId } : null;
    },
);
